"use client";

import { type RefObject, useEffect, useRef } from "react";
import { loadGsapWithScrollTrigger, shouldReduceMotion } from "@/lib/gsap";

type NumbersFlowStat = {
	value: string;
	label: string;
	description: string;
};

function useNumbersFlowAnimation(sectionRef: RefObject<HTMLElement | null>) {
	useEffect(() => {
		if (shouldReduceMotion()) {
			return;
		}

		let cleanup: (() => void) | undefined;
		let cancelled = false;

		async function setupAnimation() {
			const { gsap, ScrollTrigger } = await loadGsapWithScrollTrigger();

			if (cancelled || !sectionRef.current) {
				return;
			}

			const ctx = gsap.context(() => {
				const stats = gsap.utils.toArray<HTMLElement>(".numbers-flow-stat");
				const counters = gsap.utils.toArray<HTMLElement>(
					".numbers-flow-stat-number",
				);

				gsap.set(stats, {
					opacity: 0,
					y: 28,
					filter: "blur(8px)",
				});

				const timeline = gsap
					.timeline({
						scrollTrigger: {
							trigger: sectionRef.current,
							start: "top 85%",
							toggleActions: "play reverse restart reverse",
						},
					})
					.to(stats, {
						opacity: 1,
						y: 0,
						filter: "blur(0px)",
						duration: 0.9,
						ease: "power3.out",
						stagger: 0.12,
					});

				counters.forEach((counter, index) => {
					const prefix = counter.dataset.prefix ?? "";
					const suffix = counter.dataset.suffix ?? "";
					const target = Number(counter.dataset.countTo ?? "0");
					const animatedValue = { value: 0 };

					timeline.to(
						animatedValue,
						{
							value: target,
							duration: 1.25,
							ease: "power3.out",
							onStart: () => {
								counter.textContent = `${prefix}0${suffix}`;
							},
							onUpdate: () => {
								counter.textContent = `${prefix}${Math.round(animatedValue.value)}${suffix}`;
							},
							onComplete: () => {
								counter.textContent = `${prefix}${target}${suffix}`;
							},
							onReverseComplete: () => {
								counter.textContent = `${prefix}0${suffix}`;
							},
						},
						0.18 + index * 0.08,
					);
				});
			}, sectionRef);

			ScrollTrigger.refresh();
			cleanup = () => ctx.revert();
		}

		void setupAnimation();

		return () => {
			cancelled = true;
			cleanup?.();
		};
	}, [sectionRef]);
}

export function NumbersFlowSection() {
	const sectionRef = useRef<HTMLElement>(null);
	useNumbersFlowAnimation(sectionRef);

	const defaultStats: NumbersFlowStat[] = [
		{
			value: "10 anos",
			label: "De experiência no mercado.",
			description:
				"Tempo suficiente para saber o que funciona e o que não funciona",
		},
		{
			value: "+350",
			label: "Projetos entregues",
			description: "MVPs, plataformas e sistemas. Do simples ao complexo",
		},
		{
			value: "+120",
			label: "Clientes atendidos",
			description: "Empresas que decidiram construir com método e performance",
		},
		{
			value: "+ 8mi",
			label: "Usuários impactados",
			description: "Usuários impactados pelos produtos que ajudamos a criar",
		},
	];

	function parseStatCounter(value: string) {
		const match = value.match(/^(\D*?)(\d+(?:[.,]\d+)?)(.*)$/);

		if (!match) {
			return {
				prefix: "",
				target: 0,
				suffix: value,
			};
		}

		return {
			prefix: match[1] ?? "",
			target: Number((match[2] ?? "0").replace(",", ".")),
			suffix: match[3] ?? "",
		};
	}

	return (
		<section
			ref={sectionRef}
			className="grid gap-8 md:grid-cols-2 md:gap-10 xl:grid-cols-4 xl:gap-12 justify-between"
		>
			{defaultStats.map((stat) => {
				const counter = parseStatCounter(stat.value);
				return (
					<div
						className="numbers-flow-stat font-bricolage max-w-fit"
						key={`${stat.value}-${stat.label}`}
					>
						<p className="text-[42px] leading-none text-primary-300 md:text-[48px] xl:text-[56px]">
							<span
								className="numbers-flow-stat-number"
								data-count-to={counter.target}
								data-prefix={counter.prefix}
								data-suffix={counter.suffix}
							>
								{stat.value}
							</span>
						</p>
						<h3 className="mt-2 text-white">{stat.label}</h3>
						<div className="my-7 h-px w-full bg-primary-300/35" />
						<p className="max-w-64 text-sm text-gray-100/70">
							{stat.description}
						</p>
					</div>
				);
			})}
		</section>
	);
}
